/**
 * Training Profiles Service
 * Handles management and learner access for heat pump simulation training profiles.
 *
 * Training profiles define UI complexity, fixed simulation values, DB-driven tutorial
 * quests, AST/jsonb quest completion conditions, and assigned QuizCollections for
 * skill quizzes.
 */
import api from './apiClient';

/**
 * Get published training profiles available to learners.
 * @param {Object} params - Optional filters ({ countryId })
 * @param {Object} options - Request options
 * @returns {Promise<Array>}
 */
export const getAvailableTrainingProfiles = async (params = {}, options = {}) => {
  return api.get('/training-profiles/available', params, options);
};

/**
 * Get the published training profile library for cross-feature assignments.
 * @param {Object} options - Request options
 * @returns {Promise<Array>}
 */
export const getPublishedTrainingProfileLibrary = async (options = {}) => {
  return getAvailableTrainingProfiles({}, options);
};

/**
 * Get a published training profile for simulation playback.
 * @param {number} id - Training profile ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>}
 */
export const getPublicTrainingProfile = async (id, options = {}) => {
  return api.get(`/training-profiles/${id}/public`, {}, options);
};

/**
 * Get paginated training profiles visible to the current manager.
 * @param {Object} params - Query params (page, pageSize, search, countryId, isPublished)
 * @param {Object} options - Request options
 * @returns {Promise<Object>}
 */
export const getTrainingProfiles = async (params = {}, options = {}) => {
  return api.get('/training-profiles', params, options);
};

/**
 * Get a full training profile editor detail record.
 * @param {number} id - Training profile ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>}
 */
export const getTrainingProfile = async (id, options = {}) => {
  return api.get(`/training-profiles/${id}`, {}, options);
};

/**
 * Get quiz collections available for assignment to training profiles.
 * @param {Object} options - Request options
 * @returns {Promise<Array>}
 */
export const getTrainingProfileAvailableQuizCollections = async (options = {}) => {
  return api.get('/training-profiles/quiz-collections/available', {}, options);
};

/**
 * Create a training profile.
 * @param {Object} data - Profile data
 * @param {Object} options - Request options
 * @returns {Promise<Object>}
 */
export const createTrainingProfile = async (data, options = {}) => {
  return api.post('/training-profiles', data, options);
};

/**
 * Update a training profile.
 * @param {number} id - Training profile ID
 * @param {Object} data - Updated profile data
 * @param {Object} options - Request options
 * @returns {Promise<Object>}
 */
export const updateTrainingProfile = async (id, data, options = {}) => {
  return api.put(`/training-profiles/${id}`, data, options);
};

/**
 * Delete a training profile.
 * @param {number} id - Training profile ID
 * @param {Object} options - Request options
 * @returns {Promise<void>}
 */
export const deleteTrainingProfile = async (id, options = {}) => {
  return api.delete(`/training-profiles/${id}`, options);
};

/**
 * Publish a training profile.
 * @param {number} id - Training profile ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>}
 */
export const publishTrainingProfile = async (id, options = {}) => {
  return api.post(`/training-profiles/${id}/publish`, {}, options);
};

/**
 * Unpublish a training profile.
 * @param {number} id - Training profile ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>}
 */
export const unpublishTrainingProfile = async (id, options = {}) => {
  return api.post(`/training-profiles/${id}/unpublish`, {}, options);
};

/**
 * Upload a learning resource to a training profile.
 * Supported by the backend for PDF and MP4 files.
 * @param {number} profileId - Training profile ID
 * @param {File} file - Resource file to upload
 * @param {Object} [metadata={}] - Optional metadata
 * @param {string} [metadata.description] - Optional description
 * @param {Function} [onProgress] - Upload progress callback
 * @param {Object} [options] - Request options
 * @returns {Promise<Object>}
 */
export const uploadTrainingProfileLearningResource = async (profileId, file, metadata = {}, onProgress = null, options = {}) => {
  const formData = new FormData();
  formData.append('file', file);

  if (metadata.description) {
    formData.append('description', metadata.description);
  }

  return api.upload(`/training-profiles/${profileId}/learning-resources`, formData, {
    ...options,
    onUploadProgress: onProgress
      ? (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      : undefined,
  });
};

/**
 * Delete a learning resource from a training profile.
 * @param {number} profileId - Training profile ID
 * @param {number} fileId - File ID
 * @param {Object} [options] - Request options
 * @returns {Promise<void>}
 */
export const deleteTrainingProfileLearningResource = async (profileId, fileId, options = {}) => {
  return api.delete(`/training-profiles/${profileId}/learning-resources/${fileId}`, {
    ...options,
    successMessage: 'Learning resource removed successfully',
  });
};

/**
 * Add a tutorial quest to a training profile.
 * @param {number} profileId - Training profile ID
 * @param {Object} data - Quest data
 * @param {Object} options - Request options
 * @returns {Promise<Object>}
 */
export const addTrainingProfileQuest = async (profileId, data, options = {}) => {
  return api.post(`/training-profiles/${profileId}/quests`, data, options);
};

/**
 * Update a tutorial quest.
 * @param {number} questId - Tutorial quest ID
 * @param {Object} data - Quest data
 * @param {Object} options - Request options
 * @returns {Promise<Object>}
 */
export const updateTrainingProfileQuest = async (questId, data, options = {}) => {
  return api.put(`/training-profiles/quests/${questId}`, data, options);
};

/**
 * Delete a tutorial quest.
 * @param {number} questId - Tutorial quest ID
 * @param {Object} options - Request options
 * @returns {Promise<void>}
 */
export const deleteTrainingProfileQuest = async (questId, options = {}) => {
  return api.delete(`/training-profiles/quests/${questId}`, options);
};

/**
 * Add a step to a tutorial quest.
 * @param {number} questId - Tutorial quest ID
 * @param {Object} data - Step data
 * @param {Object} options - Request options
 * @returns {Promise<Object>}
 */
export const addTrainingProfileStep = async (questId, data, options = {}) => {
  return api.post(`/training-profiles/quests/${questId}/steps`, data, options);
};

/**
 * Update a tutorial step.
 * @param {number} stepId - Tutorial step ID
 * @param {Object} data - Step data
 * @param {Object} options - Request options
 * @returns {Promise<Object>}
 */
export const updateTrainingProfileStep = async (stepId, data, options = {}) => {
  return api.put(`/training-profiles/steps/${stepId}`, data, options);
};

/**
 * Delete a tutorial step.
 * @param {number} stepId - Tutorial step ID
 * @param {Object} options - Request options
 * @returns {Promise<void>}
 */
export const deleteTrainingProfileStep = async (stepId, options = {}) => {
  return api.delete(`/training-profiles/steps/${stepId}`, options);
};

/**
 * Bulk import tutorial quests with nested steps into a training profile.
 * @param {number} profileId - Training profile ID
 * @param {Array<Object>} quests - Array of quest payloads with nested step data
 * @param {Object} options - Request options
 * @returns {Promise<{totalCount: number, successCount: number, importedStepCount: number, errors: Array}>}
 */
export const bulkAddTrainingProfileQuests = async (profileId, quests, options = {}) => {
  return api.post(`/training-profiles/${profileId}/quests/bulk`, { quests }, options);
};

/**
 * Replace all assigned QuizCollections for a training profile.
 * @param {number} profileId - Training profile ID
 * @param {Array<{quizCollectionId: number, orderIndex?: number, displayLabel?: string}>} assignments - Assignments
 * @param {Object} options - Request options
 * @returns {Promise<Array>}
 */
export const updateTrainingProfileQuizAssignments = async (profileId, assignments, options = {}) => {
  return api.put(`/training-profiles/${profileId}/quiz-assignments`, { assignments }, options);
};

const trainingProfilesService = {
  getAvailableTrainingProfiles,
  getPublicTrainingProfile,
  getTrainingProfiles,
  getTrainingProfile,
  getTrainingProfileAvailableQuizCollections,
  createTrainingProfile,
  updateTrainingProfile,
  deleteTrainingProfile,
  publishTrainingProfile,
  unpublishTrainingProfile,
  uploadTrainingProfileLearningResource,
  deleteTrainingProfileLearningResource,
  addTrainingProfileQuest,
  updateTrainingProfileQuest,
  deleteTrainingProfileQuest,
  addTrainingProfileStep,
  updateTrainingProfileStep,
  deleteTrainingProfileStep,
  bulkAddTrainingProfileQuests,
  updateTrainingProfileQuizAssignments,
};

export default trainingProfilesService;
